import { prisma, OrderType, TriggerEvent } from "database";
import {
  createProfileV2,
  findOrCreateScopedUser,
  type ScopedUserFields,
  WHITE_LABEL_REQUIRED_MESSAGE,
} from "database/services";
import crypto from "crypto";
import { SERVICES_URLS } from "@sajilni/config";
import {
  resolveSignupPassword,
  sanitizeOptionalId,
  sanitizeString,
} from "./signup-utils";

type SignUpArgs = {
  email: string;
  password?: string;
  phoneNumber?: string;
  name?: string;
  profileData?: Record<string, any>;
  eventId?: string;
  orderId?: string;
  whiteLabelId?: string;
  participantGroupId?: string;
  orderType?: OrderType;
};

type SignUpResult = Promise<{ user: unknown; profile: unknown }>;

export const signUp = async (args: SignUpArgs): SignUpResult => {
  try {
    const {
      email,
      password,
      phoneNumber,
      name,
      profileData,
      eventId: rawEventId,
      orderId: rawOrderId,
      whiteLabelId: rawWhiteLabelId,
      orderType: rawOrderType,
    } = args;

    const eventId = sanitizeOptionalId(rawEventId);
    const orderId = sanitizeOptionalId(rawOrderId);
    const whiteLabelId = sanitizeOptionalId(rawWhiteLabelId);
    const orderType = sanitizeString(rawOrderType) as OrderType | undefined;

    if (!email) {
      throw new Error("Email is required");
    }
    if (!whiteLabelId) {
      throw new Error(WHITE_LABEL_REQUIRED_MESSAGE);
    }

    const normalizedEmail = email.toLowerCase();

    const existingUser = await prisma.user.findUnique({
      where: {
        email_accessibleWhiteLabelId: {
          email: normalizedEmail,
          accessibleWhiteLabelId: whiteLabelId,
        },
      },
    });

    if (existingUser?.lastLoginAt) {
      throw new Error("A user with this email already exists");
    }

    const isUnusedRetry = Boolean(existingUser);
    const providedPassword = sanitizeString(password);
    const hashPassword = (plain: string) =>
      crypto
        .createHmac("md5", process.env["SECRET_HASH_PASSWORD"] as string)
        .update(plain)
        .digest("hex");
    let user: ScopedUserFields;
    if (!existingUser) {
      user = await findOrCreateScopedUser({
        email: normalizedEmail,
        whiteLabelId,
        name: name || "",
        phoneNumber: phoneNumber || profileData?.phone || null,
        password: hashPassword(resolveSignupPassword(password)),
        active: true,
      });
    } else if (providedPassword && providedPassword.length >= 8) {
      user = await prisma.user.update({
        where: { id: existingUser.id },
        data: { password: hashPassword(providedPassword) },
      });
    } else {
      user = existingUser;
    }

    let createdProfile: Awaited<ReturnType<typeof createProfileV2>> | null =
      null;

    if (eventId) {
      createdProfile = await createProfileV2(
        {
          ...(profileData || {}),
          email: normalizedEmail,
          name: name || "",
          ...(phoneNumber || profileData?.phone
            ? { phoneNumber: phoneNumber || profileData?.phone }
            : {}),
        },
        eventId,
        orderId,
        user.id,
        whiteLabelId,
        orderType,
        false
      );
    }

    let url: string | undefined;
    let magic_link: string | undefined;
    if (createdProfile?.entityType && eventId) {
      const event = await prisma.event.findUnique({
        where: { id: eventId },
        select: {
          slug: true,
          whiteLabel: { select: { subdomain: true } },
        },
      });
      const basDomain = `${event?.whiteLabel?.subdomain}.${process.env["NEXT_PUBLIC_ROOT_DOMAIN"]}`;
      url = `${basDomain}/set-password?type=${createdProfile.entityType.toUpperCase()}&eventId=${
        event?.slug
      }&token=${createdProfile.token}`;
      magic_link = `${basDomain}/login?type=${createdProfile.entityType.toUpperCase()}&eventId=${
        event?.slug
      }&eventSlug=${event?.slug}&token=${createdProfile.token}`;
    }

    if (eventId && createdProfile && !isUnusedRetry) {
      const localTenantUrls = process.env.NODE_ENV === "development";

      // Fire-and-forget: do not block signup/auth on notification delivery.
      void fetch(
        `${SERVICES_URLS.MAIN_APP}/api/events/${eventId}/fire-trigger`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            trigger: TriggerEvent.PROFILE_CREATION,
            data: {
              users: [{ ...user }],
              merge_tags: {
                ...user,
                ...createdProfile,
                set_password_url: url,
                magic_link: localTenantUrls
                  ? `http://${magic_link}`
                  : `https://${magic_link}`,
              },
            },
          }),
        }
      ).catch(() => {});
    }

    return { user, profile: createdProfile };
  } catch (error) {
    console.error("Error signing up:", error);
    throw error;
  }
};
